import { useEffect, useState } from "react";
import { Send, Zap } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CONTACT } from "@/lib/site";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEVICE_TYPES = ["Smartphone", "Tablette", "PC / Mac", "Autre"];
const ISSUES = ["Écran", "Batterie", "Charge", "Caméra", "Ne s'allume plus", "Oxydation / eau", "Lent / logiciel", "Autre"];

const EMPTY = {
  name: "", phone: "", email: "",
  device_type: "Smartphone", device_model: "", issue: "Écran", message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, message: e.detail?.message || "", device_type: "Smartphone", issue: "Autre" }));
    window.addEventListener("prefill-devis", onPrefill);
    return () => window.removeEventListener("prefill-devis", onPrefill);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Demande envoyée ! Nous revenons vers vous très vite — souvent en moins d'une heure ouvrée.", {
        duration: 6000,
      });
      setForm(EMPTY);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        `L'envoi a échoué${status ? ` (erreur ${status})` : ""}. Réessayez dans un instant ou appelez-nous directement au ${CONTACT.phoneDisplay}.`,
        { duration: 6000 }
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" className="relative overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-neon/10 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-4xl px-5 lg:px-8">
        <SectionHead
          tag="Demande de devis"
          title={<>Décrivez la panne, <span className="text-neon">on s'occupe du reste</span></>}
          sub="Réponse en moins d'une heure ouvrée. Devis gratuit, sans engagement — votre demande part directement dans notre boîte."
          center
        />

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="mt-14 grid gap-5 rounded-3xl border border-line bg-ink p-7 sm:grid-cols-2 lg:p-10"
          >
            <label className="flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Nom & prénom *</span>
              <input data-testid="quote-input-name" required minLength={2} maxLength={120} autoComplete="name" className="field-input" placeholder="Camille Dupont" value={form.name} onChange={set("name")} />
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Téléphone *</span>
              <input data-testid="quote-input-phone" required minLength={6} maxLength={30} type="tel" autoComplete="tel" inputMode="tel" className="field-input" placeholder="06 12 34 56 78" value={form.phone} onChange={set("phone")} />
            </label>
            <label className="flex flex-col gap-2 sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Email *</span>
              <input data-testid="quote-input-email" required type="email" autoComplete="email" className="field-input" placeholder="vous@exemple.fr" value={form.email} onChange={set("email")} />
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Type d'appareil *</span>
              <select data-testid="quote-input-device" className="field-input" value={form.device_type} onChange={set("device_type")}>
                {DEVICE_TYPES.map((d) => <option key={d}>{d}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Marque & modèle</span>
              <input data-testid="quote-input-model" maxLength={120} className="field-input" placeholder="Ex. iPhone 13 · Samsung S22" value={form.device_model} onChange={set("device_model")} />
            </label>
            <label className="flex flex-col gap-2 sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Panne constatée *</span>
              <select data-testid="quote-input-issue" className="field-input" value={form.issue} onChange={set("issue")}>
                {ISSUES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-2 sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Décrivez le problème</span>
              <textarea data-testid="quote-input-message" maxLength={3000} className="field-input min-h-[130px] resize-y" placeholder="Ce qui s'est passé, depuis quand, ce que vous avez déjà essayé…" value={form.message} onChange={set("message")} />
            </label>

            <div className="flex flex-col items-start justify-between gap-4 sm:col-span-2 sm:flex-row sm:items-center">
              <p className="text-xs leading-relaxed text-slate-500">
                En envoyant, vous acceptez d'être recontacté au sujet de votre demande.
                <br />
                Pressé ? Appelez le{" "}
                <a href={CONTACT.phoneHref} className="font-bold text-neon hover:underline">
                  {CONTACT.phoneDisplay}
                </a>
              </p>
              <button
                data-testid="quote-form-submit"
                type="submit"
                disabled={sending}
                className="flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-extrabold uppercase tracking-wider text-night chip-glow transition-all duration-200 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-night/30 border-t-night" /> : <Send size={16} />}
                {sending ? "Envoi en cours…" : "Envoyer ma demande"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
